/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { PrimaryButton } from '@fluentui/react/lib/Button';
import { Checkbox } from '@fluentui/react/lib/Checkbox';
import { Stack } from '@fluentui/react/lib/Stack';
import { observer } from 'mobx-react';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { ExternalLink } from 'src/core/components/ExternalLink';
import { LuisText } from 'src/core/components/LuisText';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { logAcknowledgementClicked, logRunModelClicked } from 'src/core/services/telemetry/eventsNamesTemplate';
import { AzureCsAccountKind } from 'src/core/stores/AzureCsAccountStore';
import { getScopedTheme } from 'src/core/themes/luisStylist';
import { Region } from 'src/core/types/regionTypes';
import { TelemetryFeatureTitle } from 'src/core/types/telemetryTypes';
import { useAzureResourceAcknowledger } from 'src/languageStudio/core/hooks/useAzureResourceAcknowledger';
import { validateLanguageStudioResource, validateTranslatorResource } from 'src/languageStudio/core/utils/demosUtils';

export type DemoPageAgreementProps = {
    pricingLink: string;
    featureName?: TelemetryFeatureTitle;
    isPaidDemo?: boolean;
    isRunDisabled: boolean;
    onRunDemoClick: () => void;
    supportedRegions?: Region[];
    acceptedResourceType?: AzureCsAccountKind;
};

const messages = defineMessages({
    run: { id: 'languageStudio.demoPageAgreement.run', defaultMessage: 'Run' },
    viewPricingDetails: { id: 'languageStudio.demoPageAgreement.viewPricingDetails', defaultMessage: 'View pricing details' },
    resourceAcknowledgment: {
        id: 'languageStudio.demoPageAgreement.resourceAcknowledgment',
        defaultMessage: 'I acknowledge that running this demo will incur usage and may incur costs to my Azure resource.'
    }
});

const theme = getScopedTheme('DemoPage');

export const DemoPageAgreement = observer((props: DemoPageAgreementProps) => {
    const { pricingLink, isRunDisabled, isPaidDemo, featureName, supportedRegions, acceptedResourceType, onRunDemoClick } = props;
    const { localize } = useLocalization();
    const { rootStore } = useGlobalState();
    const { isSignedIn } = rootStore.authStore;
    const { csAccountStore } = rootStore.azureStore.session;
    const { isAcknowledged, changeAcknowledgement } = useAzureResourceAcknowledger();

    let isDisabled = false;

    switch (acceptedResourceType) {
        case 'TextAnalytics':
            isDisabled = !validateLanguageStudioResource({ isPaidDemo, supportedRegions, resource: csAccountStore, localize }).isValid;
            break;
        case 'TextTranslation':
            isDisabled = !validateTranslatorResource({ resource: csAccountStore, localize }).isValid;
            break;
        default:
    }

    const handleRunDemo = () => {
        const resultsStep = document.querySelector('#demoPageResultsStep');
        resultsStep?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        logRunModelClicked(featureName);
        onRunDemoClick();
    };

    const handleChangeAcknowledgement = (checked: boolean) => {
        changeAcknowledgement(checked);

        if (checked) {
            logAcknowledgementClicked(featureName);
        }
    };

    return (
        <Stack horizontalAlign="start" tokens={{ childrenGap: 20, padding: '20px 0 0 0' }}>
            {isSignedIn && (
                <Checkbox
                    disabled={isDisabled}
                    checked={isAcknowledged}
                    onChange={(_, checked) => handleChangeAcknowledgement(checked)}
                    onRenderLabel={() => (
                        <Stack horizontal data-automation-id="demoAgreementCheckbox">
                            <LuisText variant="medium" fontWeight={500} color={theme.requiredColor}>
                                *
                            </LuisText>
                            &nbsp;
                            <LuisText>{localize(messages.resourceAcknowledgment)}</LuisText>
                            &nbsp;
                            <ExternalLink href={pricingLink}>{localize(messages.viewPricingDetails)}</ExternalLink>
                        </Stack>
                    )}
                />
            )}

            <PrimaryButton
                onClick={handleRunDemo}
                text={localize(messages.run)}
                data-automation-id="demoRunButton"
                disabled={(isSignedIn && !isAcknowledged) || isRunDisabled}
            />
        </Stack>
    );
});
